import { GameScene, InputState, SceneType } from '../types/game.types';
import { RenderManager } from '../core/RenderManager';
import { AudioManager } from '../core/AudioManager';
import { BaseGame } from '../games/BaseGame';
import { GalacticDefenseGame } from '../games/galactic-defense/GalacticDefenseGame';
import { MazeRunnerGame } from '../games/maze-runner/MazeRunnerGame';
import { DefenderGame } from '../games/defender/DefenderGame';
import { CentipedeGame } from '../games/centipede/CentipedeGame';
import { AsteroidsGame } from '../games/asteroids/AsteroidsGame';
import { BlockDropGame } from '../games/block-drop/BlockDropGame';
import { RiverCrossingGame } from '../games/river-crossing/RiverCrossingGame';
import { TunnelBlasterGame } from '../games/tunnel-blaster/TunnelBlasterGame';
import { BreakoutGame } from '../games/breakout/BreakoutGame';

interface GameSceneCallbacks {
  switchScene: (scene: SceneType) => void;
}

export class GameSceneManager implements GameScene {
  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private callbacks: GameSceneCallbacks;
  private currentGame: BaseGame | null = null;
  private currentGameId: string = '';
  private isMobile: boolean = false;

  constructor(renderManager: RenderManager, audioManager: AudioManager, callbacks: GameSceneCallbacks) {
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.callbacks = callbacks;
  }

  public init(): void {
    console.log('Game Scene Manager initialized');
    // Don't initialize game here - wait for startGame to be called
    
    // Detect mobile
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                    ('ontouchstart' in window) ||
                    (navigator.maxTouchPoints > 0);
  }

  public startGame(gameId: string): void {
    console.log(`Starting game: ${gameId}`);

    // Clean up current game
    if (this.currentGame) {
      this.currentGame.cleanup();
    }

    // Create new game based on ID
    this.currentGameId = gameId;
    this.currentGame = this.createGame(gameId);

    if (this.currentGame) {
      this.currentGame.init();

      // Set up mobile controls for this game
      if ((window as any).inputManager) {
        (window as any).inputManager.setupGameControls(gameId);
      }

      // Notify app of current game for orientation check
      if ((window as any).setCurrentGame) {
        (window as any).setCurrentGame(gameId);
      }
    }
  }

  private createGame(gameId: string): BaseGame | null {
    switch (gameId) {
      case 'galactic-defense':
        return new GalacticDefenseGame(this.renderManager, this.audioManager);
      
      case 'maze-runner':
        return new MazeRunnerGame(this.renderManager, this.audioManager);
      
      case 'defender':
        return new DefenderGame(this.renderManager, this.audioManager);
      
      case 'centipede':
        return new CentipedeGame(this.renderManager, this.audioManager);
      
      case 'space-debris':
        return new AsteroidsGame(this.renderManager, this.audioManager);
      
      case 'block-drop':
        return new BlockDropGame(this.renderManager, this.audioManager);
      
      case 'river-crossing':
        return new RiverCrossingGame(this.renderManager, this.audioManager);
      
      case 'tunnel-blaster':
        return new TunnelBlasterGame(this.renderManager, this.audioManager);
      
      case 'breakout':
        return new BreakoutGame(this.renderManager, this.audioManager);

      default:
        console.error(`Unknown game ID: ${gameId}`);
        return null;
    }
  }

  private showNotImplementedMessage(gameId: string): void {
    // Show a message for 2 seconds then return to menu
    setTimeout(() => {
      this.callbacks.switchScene(SceneType.MENU);
    }, 2000);
  }

  public update(deltaTime: number, input: InputState): void {
    if (this.currentGame) {
      this.currentGame.updateBase(deltaTime, input);
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (this.currentGame) {
      this.currentGame.render(ctx);
      
      // Draw mobile exit button if on mobile
      if (this.isMobile) {
        this.renderMobileExitButton(ctx);
      }
    } else if (this.currentGameId) {
      // Show "not implemented" message
      this.renderNotImplementedMessage();
    }
  }
  
  private renderMobileExitButton(ctx: CanvasRenderingContext2D): void {
    const buttonSize = 40;
    const margin = 10;
    const x = ctx.canvas.width - buttonSize - margin;
    const y = margin;
    
    // Draw button background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(x, y, buttonSize, buttonSize);
    
    // Draw button border
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, buttonSize, buttonSize);
    
    // Draw X symbol
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 3;
    const padding = 10;
    ctx.beginPath();
    ctx.moveTo(x + padding, y + padding);
    ctx.lineTo(x + buttonSize - padding, y + buttonSize - padding);
    ctx.moveTo(x + buttonSize - padding, y + padding);
    ctx.lineTo(x + padding, y + buttonSize - padding);
    ctx.stroke();
  }

  private renderNotImplementedMessage(): void {
    this.renderManager.clear('#40318D');
    
    const centerX = 400;
    const centerY = 300;
    
    this.renderManager.drawTextCentered(
      'COMING SOON!', 
      centerX, 
      centerY - 40, 
      '#BFCE72', 
      '32px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      `${this.currentGameId.toUpperCase()} IS NOT YET IMPLEMENTED`, 
      centerX, 
      centerY, 
      '#FFFFFF', 
      '16px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      'RETURNING TO MENU...', 
      centerX, 
      centerY + 40, 
      '#787878', 
      '14px "Courier New", monospace'
    );
  }

  public handleInput(input: InputState): void {
    // Handle ESC key to return to menu from any game
    if (input.keys['Escape']) {
      this.callbacks.switchScene(SceneType.MENU);
      return;
    }
    
    // Handle mobile exit button touch
    if (this.isMobile && input.mouse.buttons[0]) {
      const buttonSize = 40;
      const margin = 10;
      const canvas = this.renderManager.getCanvas();
      const x = canvas.width - buttonSize - margin;
      const y = margin;
      
      // Check if click is within exit button bounds
      if (input.mouse.position.x >= x && input.mouse.position.x <= x + buttonSize &&
          input.mouse.position.y >= y && input.mouse.position.y <= y + buttonSize) {
        this.callbacks.switchScene(SceneType.MENU);
        return;
      }
    }
    
    // Also check touch input for mobile
    if (this.isMobile && input.touch.active && input.touch.gesture === 'tap') {
      const buttonSize = 40;
      const margin = 10;
      const canvas = this.renderManager.getCanvas();
      const x = canvas.width - buttonSize - margin;
      const y = margin;
      
      // Check if touch is within exit button bounds
      if (input.touch.position.x >= x && input.touch.position.x <= x + buttonSize &&
          input.touch.position.y >= y && input.touch.position.y <= y + buttonSize) {
        this.callbacks.switchScene(SceneType.MENU);
        return;
      }
    }
    
    if (this.currentGame) {
      this.currentGame.handleInput(input);
    }
  }

  public cleanup(): void {
    console.log('Game Scene Manager cleaned up');

    if (this.currentGame) {
      this.currentGame.cleanup();
      this.currentGame = null;
    }

    this.currentGameId = '';

    // Clear current game for orientation check
    if ((window as any).setCurrentGame) {
      (window as any).setCurrentGame(null);
    }
  }

  public getCurrentGame(): BaseGame | null {
    return this.currentGame;
  }

  public getCurrentGameId(): string {
    return this.currentGameId;
  }
}